import React, { useRef, useState } from "react";
import api from "../../lib/api";
import { apiError } from "../../lib/format";
import { toast } from "sonner";
import { Database, DownloadSimple, UploadSimple, Warning, FileArrowUp, ShieldCheck, Trash, UsersThree } from "@phosphor-icons/react";

const CONFIRM_WORD = "RESTAURAR";
const PURGE_WORD = "ELIMINAR";

export default function AdminBackup() {
  const [downloading, setDownloading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [file, setFile] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);
  const [purging, setPurging] = useState(false);
  const [purgeConfirm, setPurgeConfirm] = useState("");
  const [purgeResult, setPurgeResult] = useState(null);

  const purgeClients = async () => {
    if (purgeConfirm.trim().toUpperCase() !== PURGE_WORD) {
      toast.error(`Escribe ${PURGE_WORD} para confirmar`);
      return;
    }
    setPurging(true);
    setPurgeResult(null);
    try {
      const r = await api.post(`/admin/purge-clients?confirm=${PURGE_WORD}`);
      setPurgeResult(r.data.deleted || {});
      setPurgeConfirm("");
      toast.success("Todos los clientes fueron eliminados");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setPurging(false);
    }
  };

  const download = async () => {
    setDownloading(true);
    try {
      const r = await api.get("/admin/backup", { responseType: "blob" });
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const name = m ? m[1] : `activa-backup-${Date.now()}.json`;
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Respaldo descargado");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDownloading(false);
    }
  };

  const onPick = (e) => {
    setFile(e.target.files?.[0] || null);
    setResult(null);
  };

  const restore = async () => {
    if (!file) { toast.error("Selecciona un archivo de respaldo"); return; }
    if (confirmText.trim().toUpperCase() !== CONFIRM_WORD) {
      toast.error(`Escribe ${CONFIRM_WORD} para confirmar`);
      return;
    }
    setRestoring(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/admin/restore?confirm=${CONFIRM_WORD}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      toast.success("Respaldo restaurado correctamente");
      setFile(null);
      setConfirmText("");
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRestoring(false);
    }
  };

  const canRestore = file && confirmText.trim().toUpperCase() === CONFIRM_WORD && !restoring;

  return (
    <div className="space-y-8" data-testid="admin-backup-page">
      <header>
        <div className="label-mono mb-2">Administración</div>
        <h1 className="font-display text-4xl font-black tracking-tighter flex items-center gap-3">
          <Database size={36} weight="duotone" /> Respaldo y restauración
        </h1>
        <p className="text-inkMuted text-sm mt-2 max-w-2xl">
          Descarga una copia de seguridad completa de la app (usuarios, saldos, movimientos,
          transferencias, créditos, chats y auditoría) o restáurala desde un archivo previo.
        </p>
      </header>

      {/* Descargar */}
      <div className="bento p-6 space-y-4" data-testid="backup-download-card">
        <div className="flex items-center gap-3">
          <DownloadSimple size={22} weight="duotone" />
          <h2 className="font-display text-lg font-bold">Descargar respaldo</h2>
        </div>
        <p className="text-sm text-inkMuted max-w-2xl">
          Genera un archivo <code className="font-mono text-xs">.json</code> con todos los datos
          actuales. Guárdalo en un lugar seguro. Por seguridad, los PIN no se incluyen en el archivo.
        </p>
        <button
          onClick={download}
          disabled={downloading}
          data-testid="backup-download-btn"
          className="h-11 px-5 bg-forest text-bg text-sm font-semibold flex items-center gap-2 hover:bg-forest-hover disabled:opacity-50"
        >
          <DownloadSimple size={18} weight="bold" />
          {downloading ? "Generando…" : "Descargar copia de seguridad"}
        </button>
      </div>

      {/* Restaurar */}
      <div className="bento p-6 space-y-5" data-testid="backup-restore-card">
        <div className="flex items-center gap-3">
          <UploadSimple size={22} weight="duotone" />
          <h2 className="font-display text-lg font-bold">Restaurar desde respaldo</h2>
        </div>

        <div className="flex items-start gap-3 border border-danger/40 bg-danger/5 p-4">
          <Warning size={22} weight="fill" className="text-danger shrink-0 mt-0.5" />
          <p className="text-sm text-ink">
            <strong>Acción destructiva.</strong> Restaurar <strong>reemplaza por completo</strong>{" "}
            todos los datos actuales por los del archivo. Esto no se puede deshacer. Los PIN
            actuales de los usuarios se conservan para que puedan seguir iniciando sesión.
          </p>
        </div>

        <div className="space-y-2">
          <label className="label-mono block">Archivo de respaldo (.json)</label>
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            onChange={onPick}
            data-testid="backup-file-input"
            className="block w-full text-sm text-inkMuted file:mr-4 file:border-0 file:bg-forest file:px-4 file:py-2 file:text-sm file:font-semibold file:text-bg hover:file:bg-forest-hover"
          />
          {file && (
            <p className="text-xs text-inkMuted flex items-center gap-1.5" data-testid="backup-file-name">
              <FileArrowUp size={14} /> {file.name} · {(file.size / 1024).toFixed(0)} KB
            </p>
          )}
        </div>

        <div className="space-y-2 max-w-md">
          <label className="label-mono block">
            Escribe <span className="text-danger font-bold">{CONFIRM_WORD}</span> para confirmar
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={CONFIRM_WORD}
            data-testid="backup-confirm-input"
            className="w-full h-11 border border-hairline bg-surface px-4 text-sm font-mono tracking-widest focus:outline-none focus:border-forest"
          />
        </div>

        <button
          onClick={restore}
          disabled={!canRestore}
          data-testid="backup-restore-btn"
          className="h-11 px-5 bg-danger text-bg text-sm font-semibold flex items-center gap-2 hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <UploadSimple size={18} weight="bold" />
          {restoring ? "Restaurando…" : "Restaurar respaldo"}
        </button>

        {result && (
          <div className="border border-hairline p-4 space-y-2" data-testid="backup-restore-result">
            <div className="flex items-center gap-2 text-sm font-bold">
              <ShieldCheck size={18} weight="fill" className="text-success" />
              Restauración completada
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs font-mono text-inkMuted">
              {Object.entries(result.restored || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-2 border-b border-hairline py-0.5">
                  <span>{k}</span><span className="text-ink font-bold">{v}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Eliminar todos los clientes */}
      <div className="bento p-6 space-y-5" data-testid="purge-clients-card">
        <div className="flex items-center gap-3">
          <UsersThree size={22} weight="duotone" />
          <h2 className="font-display text-lg font-bold">Eliminar todos los clientes</h2>
        </div>

        <div className="flex items-start gap-3 border border-danger/40 bg-danger/5 p-4">
          <Warning size={22} weight="fill" className="text-danger shrink-0 mt-0.5" />
          <p className="text-sm text-ink">
            <strong>Acción destructiva.</strong> Elimina <strong>todos los usuarios con rol cliente</strong>{" "}
            y sus datos asociados (movimientos, transferencias, créditos, chats y archivos). Se
            conservan las cuentas de <strong>administrador</strong> y <strong>asesor</strong>. Esto no se puede deshacer.
          </p>
        </div>

        <div className="space-y-2 max-w-md">
          <label className="label-mono block">
            Escribe <span className="text-danger font-bold">{PURGE_WORD}</span> para confirmar
          </label>
          <input
            type="text"
            value={purgeConfirm}
            onChange={(e) => setPurgeConfirm(e.target.value)}
            placeholder={PURGE_WORD}
            data-testid="purge-confirm-input"
            className="w-full h-11 border border-hairline bg-surface px-4 text-sm font-mono tracking-widest focus:outline-none focus:border-danger"
          />
        </div>

        <button
          onClick={purgeClients}
          disabled={purging || purgeConfirm.trim().toUpperCase() !== PURGE_WORD}
          data-testid="purge-clients-btn"
          className="h-11 px-5 bg-danger text-bg text-sm font-semibold flex items-center gap-2 hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Trash size={18} weight="bold" />
          {purging ? "Eliminando…" : "Eliminar todos los clientes"}
        </button>

        {purgeResult && (
          <div className="border border-hairline p-4 space-y-2" data-testid="purge-result">
            <div className="flex items-center gap-2 text-sm font-bold">
              <ShieldCheck size={18} weight="fill" className="text-success" />
              Clientes eliminados
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs font-mono text-inkMuted">
              {Object.entries(purgeResult).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-2 border-b border-hairline py-0.5">
                  <span>{k}</span><span className="text-ink font-bold">{v}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
